import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import {
  Users,
  Plus,
  Search,
  Edit2,
  Trash2,
  FileCheck,
  DollarSign,
  Phone,
  Mail,
  User,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Customer {
  id: string;
  name: string;
  document: string;
  email?: string;
  phone?: string;
  totalReceived: number;
  chargeCount: number;
  createdAt: string;
}

export function SupplierCustomers() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // Form state
  const [name, setName] = useState('');
  const [document, setDocument] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  // Fetch customers
  const { data: customersData, isLoading } = useQuery({
    queryKey: ['supplier', 'customers', searchTerm],
    queryFn: async () => {
      const params: any = {};
      if (searchTerm) params.search = searchTerm;
      const response = await api.get('/banking/supplier/customers', { params });
      return response.data;
    },
  });

  // Fetch customer details
  const { data: customerDetails } = useQuery({
    queryKey: ['supplier', 'customers', selectedCustomer?.id, 'details'],
    queryFn: async () => {
      const response = await api.get(`/banking/supplier/customers/${selectedCustomer?.id}`);
      return response.data;
    },
    enabled: !!selectedCustomer?.id && showDetailsModal,
  });

  // Create customer mutation
  const createCustomerMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/banking/supplier/customers', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier', 'customers'] });
      setShowModal(false);
      resetForm();
    },
  });

  // Update customer mutation
  const updateCustomerMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await api.put(`/banking/supplier/customers/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier', 'customers'] });
      setShowModal(false);
      setEditingCustomer(null);
      resetForm();
    },
  });

  // Delete customer mutation
  const deleteCustomerMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/banking/supplier/customers/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier', 'customers'] });
    },
  });

  const resetForm = () => {
    setName('');
    setDocument('');
    setEmail('');
    setPhone('');
  };

  const openEditModal = (customer: Customer) => {
    setEditingCustomer(customer);
    setName(customer.name);
    setDocument(customer.document);
    setEmail(customer.email || '');
    setPhone(customer.phone || '');
    setShowModal(true);
  };

  const openDetailsModal = (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowDetailsModal(true);
  };

  const handleSave = () => {
    const data = { name, document, email, phone };
    if (editingCustomer) {
      updateCustomerMutation.mutate({ id: editingCustomer.id, data });
    } else {
      createCustomerMutation.mutate(data);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(date));
  };

  const formatDocument = (doc: string) => {
    if (doc.length === 11) {
      return doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (doc.length === 14) {
      return doc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return doc;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Clientes</h1>
          <p className="text-surface-400">Gerencie seus clientes</p>
        </div>
        <Button
          variant="primary"
          onClick={() => {
            setEditingCustomer(null);
            resetForm();
            setShowModal(true);
          }}
        >
          <Plus size={20} className="mr-2" />
          Novo Cliente
        </Button>
      </div>

      {/* Search */}
      <Card>
        <div className="p-4">
          <Input
            placeholder="Buscar por nome, CPF/CNPJ ou e-mail..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftIcon={<Search size={18} className="text-surface-400" />}
          />
        </div>
      </Card>

      {/* Customers List */}
      <Card>
        <div className="p-6">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-4 p-4 rounded-lg bg-surface-800/50 animate-pulse">
                  <div className="w-12 h-12 rounded-full bg-surface-700" />
                  <div className="flex-1">
                    <div className="h-4 w-32 bg-surface-700 rounded mb-2" />
                    <div className="h-3 w-48 bg-surface-700 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : customersData?.customers?.length > 0 ? (
            <div className="space-y-3">
              {customersData.customers.map((customer: Customer) => (
                <div
                  key={customer.id}
                  className="flex items-center gap-4 p-4 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors cursor-pointer"
                  onClick={() => openDetailsModal(customer)}
                >
                  <div className="w-12 h-12 rounded-full bg-primary-500/20 flex items-center justify-center">
                    <User size={24} className="text-primary-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white">{customer.name}</p>
                    <p className="text-xs text-surface-400">
                      {formatDocument(customer.document)}
                      {customer.email && ` • ${customer.email}`}
                    </p>
                  </div>
                  <div className="text-right hidden md:block">
                    <p className="text-sm font-semibold text-green-400">
                      {formatCurrency(customer.totalReceived)}
                    </p>
                    <p className="text-xs text-surface-400">
                      {customer.chargeCount} cobrança(s)
                    </p>
                  </div>
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/banking/charges/new?customerId=${customer.id}`)}
                      title="Nova cobrança"
                    >
                      <FileCheck size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditModal(customer)}
                      title="Editar"
                    >
                      <Edit2 size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteCustomerMutation.mutate(customer.id)}
                      title="Excluir"
                    >
                      <Trash2 size={16} className="text-red-400" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users size={48} className="mx-auto text-surface-600 mb-3" />
              <p className="text-surface-400">Nenhum cliente encontrado</p>
              <Button
                variant="primary"
                size="sm"
                className="mt-4"
                onClick={() => setShowModal(true)}
              >
                Cadastrar primeiro cliente
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingCustomer(null);
          resetForm();
        }}
        title={editingCustomer ? 'Editar Cliente' : 'Novo Cliente'}
      >
        <div className="space-y-4">
          <Input
            label="Nome completo"
            placeholder="Nome do cliente"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Input
            label="CPF/CNPJ"
            placeholder="000.000.000-00"
            value={document}
            onChange={(e) => setDocument(e.target.value)}
          />
          <Input
            label="E-mail (opcional)"
            placeholder="email@exemplo.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            label="Telefone (opcional)"
            placeholder="(00) 00000-0000"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />

          <div className="flex gap-3 pt-4">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => {
                setShowModal(false);
                setEditingCustomer(null);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={handleSave}
              disabled={!name || !document || createCustomerMutation.isPending || updateCustomerMutation.isPending}
            >
              {createCustomerMutation.isPending || updateCustomerMutation.isPending
                ? 'Salvando...'
                : 'Salvar'
              }
            </Button>
          </div>
        </div>
      </Modal>

      {/* Customer Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedCustomer(null);
        }}
        title="Detalhes do Cliente"
      >
        {selectedCustomer && (
          <div className="space-y-6">
            {/* Customer Info */}
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary-500/20 flex items-center justify-center">
                <User size={32} className="text-primary-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{selectedCustomer.name}</h3>
                <p className="text-sm text-surface-400">{formatDocument(selectedCustomer.document)}</p>
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-4">
              {selectedCustomer.email && (
                <div className="flex items-center gap-2 text-sm text-surface-300">
                  <Mail size={16} className="text-surface-500" />
                  {selectedCustomer.email}
                </div>
              )}
              {selectedCustomer.phone && (
                <div className="flex items-center gap-2 text-sm text-surface-300">
                  <Phone size={16} className="text-surface-500" />
                  {selectedCustomer.phone}
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <DollarSign size={20} className="text-green-400" />
                  <div>
                    <p className="text-xs text-surface-400">Total recebido</p>
                    <p className="text-lg font-semibold text-white">
                      {formatCurrency(selectedCustomer.totalReceived)}
                    </p>
                  </div>
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <FileCheck size={20} className="text-primary-400" />
                  <div>
                    <p className="text-xs text-surface-400">Cobranças</p>
                    <p className="text-lg font-semibold text-white">
                      {selectedCustomer.chargeCount}
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Recent Charges */}
            {customerDetails?.recentCharges?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-surface-300 mb-3">Cobranças recentes</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {customerDetails.recentCharges.map((charge: any) => (
                    <div
                      key={charge.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-surface-800/50"
                    >
                      <div>
                        <p className="text-sm text-white">{charge.description || 'Cobrança'}</p>
                        <p className="text-xs text-surface-400">{formatDate(charge.createdAt)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-white">
                          {formatCurrency(charge.amount)}
                        </p>
                        <span className={`text-xs ${
                          charge.status === 'PAID' ? 'text-green-400' :
                          charge.status === 'ACTIVE' ? 'text-blue-400' :
                          'text-red-400'
                        }`}>
                          {charge.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-surface-800">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => setShowDetailsModal(false)}
              >
                Fechar
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                onClick={() => {
                  setShowDetailsModal(false);
                  navigate(`/banking/charges/new?customerId=${selectedCustomer.id}`);
                }}
              >
                <FileCheck size={16} className="mr-2" />
                Nova Cobrança
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default SupplierCustomers;
